import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AudioMutedOutlined, AudioOutlined } from '@ant-design/icons';

import { setLocalVideoElement, setRemoteMute } from '../../store/actions';
import { setLocalMute } from '../../store/actions/InterviewActions';
import * as actions from '../../store/actions';

import styles from './NTInterviewVideoContainer.scss';

const VideoTile = ({
  tileState,
  audioVideo,
  dispatch,
  isRemoteVideoOn = false,
  isLocal = false,
  participantName,
  isMuted = false,
}) => {
  const videoRef = useRef(null);

  const setVideoRef = useCallback(
    el => {
      videoRef.current = el;
      if (el && isLocal) {
        dispatch(setLocalVideoElement(el));
      }
    },
    [isLocal, dispatch],
  );

  useEffect(() => {
    if (!tileState || !videoRef.current || !audioVideo) return;

    if (tileState.tileId) {
      audioVideo.bindVideoElement(tileState.tileId, videoRef.current);
    }
  }, [tileState, audioVideo]);

  if (!tileState) return null;

  return (
    <div className={styles.videoTile}>
      <div className={styles.videoWrapper}>
        <video
          ref={setVideoRef}
          className={styles.videoElement}
          muted={isLocal}
          playsInline
          autoPlay
          style={{
            display: (isLocal ? tileState.active : isRemoteVideoOn) ? 'block' : 'none',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {!(isLocal ? tileState.active : isRemoteVideoOn) && (
          <div className={styles.videoPlaceholder}>
            <div className={styles.avatar}>{participantName?.charAt(0)?.toUpperCase()}</div>
          </div>
        )}

        <div className={styles.muteIndicator}>
          {isMuted ? (
            <AudioMutedOutlined style={{ color: '#fff', fontSize: '16px' }} />
          ) : (
            <AudioOutlined style={{ color: '#fff', fontSize: '16px' }} />
          )}
        </div>

        <div className={styles.nameIndicator}>
          <span className={styles.participantName}>{participantName}</span>
        </div>
      </div>
    </div>
  );
};

const NTInterviewVideoContainer = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const participants = useSelector(state => state.interview.participants || []);
  const currentUserEmail = localStorage.getItem('username');

  const { meetingSession, videoTiles, localTileId, localMute, remoteMuteStatus, attendees } =
    useSelector(state => state.interviewRecording);
  const { id } = useSelector(state => state.meeting);

  useEffect(() => {
    if (meetingSession?.audioVideo) {
      try {
        meetingSession.audioVideo.startLocalVideoTile();
      } catch (error) {
        console.error('Error starting local video tile:', error);
      }
    }
  }, [meetingSession]);

  useEffect(() => {
    if (!meetingSession || !participants.length) return;

    const subscribeToRemoteMutes = async () => {
      for (const participant of participants) {
        if (currentUserEmail !== participant.email) {
          try {
            const fetchRemoteAttendee = await dispatch(actions.fetchAttendee(id, participant.id));

            const handleRemoteMute = (attendeeId, volume, muted) => {
              const remoteAttendees = Array.isArray(fetchRemoteAttendee)
                ? fetchRemoteAttendee
                : [fetchRemoteAttendee];

              remoteAttendees.forEach(att => {
                const prevMuted = remoteMuteStatus?.[att.participant_id];
                if (muted !== null && prevMuted !== muted) {
                  dispatch(setRemoteMute(att.participant_id, muted));
                }
              });
            };

            meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
              fetchRemoteAttendee.aws_attendee_id,
              handleRemoteMute,
            );
          } catch (error) {
            console.error('Error subscribing to remote mute:', error);
          }
        }
      }
    };

    subscribeToRemoteMutes();
  }, [participants, id, meetingSession, dispatch, currentUserEmail]);

  const sessionAudioVideo = meetingSession?.audioVideo;

  const remoteTiles = Object.values(attendees).filter(
    attendee =>
      attendee?.tileState &&
      !attendee?.hasLeft &&
      !attendee?.tileState?.localTile &&
      !attendee?.tileState.isContent,
  );

  const totalParticipants = (localTileId ? 1 : 0) + remoteTiles.length;

  const getGridClass = () => {
    if (totalParticipants === 1) return styles.grid1x1;
    if (totalParticipants === 2) return styles.grid1x2;
    if (totalParticipants <= 4) return styles.grid2x2;
    if (totalParticipants <= 6) return styles.grid2x3;
    return styles.grid3x3;
  };

  return (
    <div className={styles.googleMeetContainer}>
      <div className={`${styles.videoGrid} ${getGridClass()}`}>
        {localTileId && (
          <VideoTile
            key="local"
            tileState={videoTiles.get(localTileId)}
            isLocal
            audioVideo={sessionAudioVideo}
            dispatch={dispatch}
            participantName={
              participants.find(p => p.email === currentUserEmail)?.name || t('interview.you')
            }
            isMuted={localMute}
          />
        )}

        {remoteTiles.map(attendee => {
          const externalId = attendee.tileState.boundExternalUserId.split('.')[0];
          const remoteParticipant = participants.find(p => p.id === externalId);

          const participantName = remoteParticipant?.name || t('interview.participant');

          return (
            <VideoTile
              key={attendee.tileId}
              tileState={attendee.tileState}
              isRemoteVideoOn={attendee.isVideoOn}
              audioVideo={sessionAudioVideo}
              dispatch={dispatch}
              participantName={participantName}
              isMuted={remoteMuteStatus?.[remoteParticipant?.id]}
            />
          );
        })}
      </div>
    </div>
  );
};

export default NTInterviewVideoContainer;
